import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../config/firebase";

// ─── BRAND TOKENS ─────────────────────────────────────────────────────────────
const KA = {
  red:       "#E83530",
  navy:      "#233C5F",
  navyDark:  "#152540",
  gold:      "#A29164",
  goldLight: "#c4b48a",
  cream:     "#F5F0E8",
  white:     "#FFFFFF",
  offWhite:  "#FAF8F4",
};

// ─── CONFIG (Firestore, live) ─────────────────────────────────────────────────
// Subscribes to the given division's schedule doc. Edits published from the admin
// editor (or the Firebase console) appear on the TVs within seconds — no rebuild,
// no redeploy. Last-known-good config is cached in localStorage (per division) so
// a brief Firestore hiccup never blanks a hallway screen.
function useConfig(division) {
  const cacheKey = `bellScheduleConfig_${division}`;
  const [config, setConfig] = useState(() => {
    try { const c = localStorage.getItem(cacheKey); return c ? JSON.parse(c) : null; }
    catch { return null; }
  });
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "bellSchedules", division),
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        setConfig(data);
        try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch { /* quota */ }
      },
      (err) => console.error("Bell schedule config load failed:", err)
    );
    return () => unsub();
  }, [division, cacheKey]);
  return config;
}

// Shared, school-wide lunch menu (one doc for both divisions). A division uses
// this unless its own lunchMenu.linked is false. Same live + cached pattern.
const SHARED_MENU_CACHE = "sharedLunchMenu";
function useSharedMenu() {
  const [menu, setMenu] = useState(() => {
    try { const c = localStorage.getItem(SHARED_MENU_CACHE); return c ? JSON.parse(c) : null; }
    catch { return null; }
  });
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "bellSchedules", "sharedLunch"),
      (snap) => {
        if (!snap.exists()) return;
        const d = snap.data();
        setMenu(d);
        try { localStorage.setItem(SHARED_MENU_CACHE, JSON.stringify(d)); } catch { /* quota */ }
      },
      (err) => console.error("Shared lunch menu load failed:", err)
    );
    return () => unsub();
  }, []);
  return menu;
}
// ─── DATE / TIME HELPERS ──────────────────────────────────────────────────────
function parseHM(s)   { const [h, m] = String(s).split(":").map(Number); return [h, m]; }
function toDateStr(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }
function dateOnly(d)  { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
function inRanges(ds, ranges) { return (ranges || []).some((r) => ds >= r.start && ds <= r.end); }

function announceStyle(kind) {
  if (kind === "alert")     return { background: KA.red,      color: KA.white };
  if (kind === "highlight") return { background: KA.gold,     color: KA.navy  };
  return { background: KA.navyDark, color: KA.white }; // "info" (default)
}

// ─── RICH TEXT (announcements) ────────────────────────────────────────────────
function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;" }[c]));
}
// Allowlist sanitizer for admin-authored rich text shown on unauthenticated kiosks.
// Keeps only basic formatting tags, strips every attribute, drops everything else.
const ALLOWED_TAGS = { B:1, STRONG:1, I:1, EM:1, U:1, BR:1, DIV:1, P:1, SPAN:1, UL:1, OL:1, LI:1 };
function sanitizeHtml(html) {
  if (!html) return "";
  try {
    const docp = new DOMParser().parseFromString("<div>" + html + "</div>", "text/html");
    const root = docp.body.firstChild;
    const walk = (node) => {
      [...node.childNodes].forEach((ch) => {
        if (ch.nodeType === 1) {
          if (!ALLOWED_TAGS[ch.tagName]) { node.replaceChild(docp.createTextNode(ch.textContent || ""), ch); return; }
          [...ch.attributes].forEach((a) => ch.removeAttribute(a.name));
          walk(ch);
        } else if (ch.nodeType === 8) { node.removeChild(ch); }
      });
    };
    walk(root);
    return root.innerHTML;
  } catch { return escapeHtml(String(html).replace(/<[^>]*>/g, "")); }
}
const stripTags = (h) => String(h || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
function nowStamp(now) {
  return `${toDateStr(now)}T${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;
}

// The announcement to show right now, or null. New model: config.announcements =
// { enabled, entries:[{id, html, style, start:"YYYY-MM-DDTHH:MM", end:"..."}] }.
// Among entries active at `now`, the one with the LATEST start wins — so a message
// added later (an "interrupt") temporarily covers a longer-running one, and once it
// ends the longer one shows again on its own. Falls back to the legacy single
// announcement object for docs published before this change.
function activeAnnouncement(config, now) {
  const A = config?.announcements;
  if (A && A.enabled && Array.isArray(A.entries)) {
    const ns = nowStamp(now);
    const active = A.entries.filter((e) => e && e.start && e.end && e.start <= ns && ns < e.end && stripTags(sanitizeHtml(e.html)));
    if (!active.length) return null;
    active.sort((a, b) => (a.start < b.start ? 1 : a.start > b.start ? -1 : a.end < b.end ? 1 : a.end > b.end ? -1 : 0));
    return { html: sanitizeHtml(active[0].html), style: active[0].style };
  }
  const a = config?.announcement; // legacy single-message fallback
  if (a && a.enabled && (a.text || "").trim()) {
    if (a.expiresAt && toDateStr(dateOnly(now)) > a.expiresAt) return null;
    return { html: escapeHtml(a.text).replace(/\n/g, "<br>"), style: a.style };
  }
  return null;
}

// Featured speaker card: shown whenever enabled and it has content, until its
// optional show-until moment passes. Independent of the school-day gate, and it
// takes the left-column slot ahead of the lunch menu.
function featuredVisible(config, now) {
  const f = config?.featured;
  if (!f || !f.enabled) return false;
  if (!(f.name || "").trim() && !f.photo) return false;
  if (f.showUntil && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(f.showUntil) && nowStamp(now) >= f.showUntil) return false;
  return true;
}

// Lunch menu for a date, from whichever source applies (shared or a division's
// own). Each date holds an optional title and its lines.
function menuForDate(source, now) {
  if (!source || !source.enabled) return { items: [], title: undefined };
  const ds = toDateStr(dateOnly(now));
  const day = source.byDate?.[ds];
  const items = (day?.items || []).filter((it) => (it.text || "").trim());
  return { items, title: day?.title };
}

// ─── EVENT MODE (manual weekend / special-event takeover — per division) ───────
// A per-division switch (config.eventMode.enabled) that repurposes the whole
// screen for an events board: a weekend activities list, a one-off event, and/or
// a full-screen poster. Independent of the school-day schedule — on = takes over
// ANY day; off = nothing changes. An event dated today with a start time gets a
// live NOW / NEXT tag and dims once it's over. Config shape:
//   config.eventMode = {
//     enabled, title, subtitle, poster, posterFit:"contain"|"cover",
//     events:[{ id, date:"YYYY-MM-DD"?, start:"HH:MM"?, end:"HH:MM"?, title, location, note }]
//   }
function eventModeActive(config) {
  const e = config?.eventMode;
  if (!e || !e.enabled) return false;
  const hasEvents = Array.isArray(e.events) && e.events.some((ev) => ev && (ev.title || "").trim());
  const hasPoster = !!(e.poster && String(e.poster).trim());
  return hasEvents || hasPoster;
}

function fmtDateLabel(ds) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ds || "")) return "";
  const [y, m, d] = ds.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${DAYS[dt.getDay()].slice(0, 3)} · ${MONTHS[m - 1].slice(0, 3)} ${d}`;
}

// Normalizes the config into what the board renders: a time-sorted event list
// (each tagged now / next / past when it falls on today and carries a start
// time), plus the optional poster. Derived purely from config + the clock, so the
// live TVs and the editor preview stay identical.
function buildEventBoard(config, now) {
  const e = config?.eventMode || {};
  const todayStr = toDateStr(dateOnly(now));
  const nm = nowMins(now);

  const events = (e.events || [])
    .filter((ev) => ev && (ev.title || "").trim())
    .map((ev, i) => {
      const hasStart = /^\d{1,2}:\d{2}$/.test(ev.start || "");
      const hasEnd = /^\d{1,2}:\d{2}$/.test(ev.end || "");
      const [sh, sm] = hasStart ? parseHM(ev.start) : [0, 0];
      const [eh, em] = hasEnd ? parseHM(ev.end) : [0, 0];
      const dateStr = /^\d{4}-\d{2}-\d{2}$/.test(ev.date || "") ? ev.date : null;
      return {
        id: ev.id || `ev${i}`,
        title: (ev.title || "").trim(),
        location: (ev.location || "").trim(),
        note: (ev.note || "").trim(),
        dateLabel: dateStr && dateStr !== todayStr ? fmtDateLabel(dateStr) : "",
        isToday: !dateStr || dateStr === todayStr,
        hasStart,
        startM: hasStart ? toMins(sh, sm) : null,
        endM: hasEnd ? toMins(eh, em) : null,
        timeLabel: hasStart ? (hasEnd ? `${fmtTime(sh, sm)} – ${fmtTime(eh, em)}` : fmtTime(sh, sm)) : "",
        _sort: `${dateStr || "0000-00-00"}|${hasStart ? String(toMins(sh, sm)).padStart(4, "0") : "9999"}|${String(i).padStart(3, "0")}`,
      };
    })
    .sort((a, b) => (a._sort < b._sort ? -1 : a._sort > b._sort ? 1 : 0));

  events.forEach((ev) => {
    if (!ev.isToday || !ev.hasStart) { ev.status = ""; return; }
    const end = ev.endM != null ? ev.endM : ev.startM + 60;
    ev.status = nm >= ev.startM && nm < end ? "now" : nm >= end ? "past" : "";
  });
  const upNext = events.find((ev) => ev.status === "" && ev.isToday && ev.hasStart && ev.startM > nm);
  if (upNext) upNext.status = "next";

  return {
    title: (e.title || "").trim() || "Upcoming Events",
    subtitle: (e.subtitle || "").trim(),
    poster: e.poster && String(e.poster).trim() ? String(e.poster).trim() : "",
    posterFit: e.posterFit === "cover" ? "cover" : "contain",
    events,
  };
}

// The semester anchor that applies to a date = the latest anchor on or before it.
function activeAnchor(config, target) {
  const sems = [...(config.year?.semesters || [])].sort((a, b) => a.anchor.localeCompare(b.anchor));
  if (!sems.length) return null;
  const ts = toDateStr(target);
  let anchor = sems[0].anchor;
  for (const s of sems) if (s.anchor <= ts) anchor = s.anchor;
  return anchor;
}

// Does this date consume a rotation-day slot? (weekends, no-school days, and
// overrides flagged countsTowardRotation:false do not.)
function consumesRotationDay(config, d) {
  const dow = d.getDay();
  if (dow === 5 || dow === 6) return false;
  const ds = toDateStr(d);
  if (inRanges(ds, config.noSchool)) return false;
  const ov = config.overrides?.[ds];
  if (ov && ov.countsTowardRotation === false) return false;
  return true;
}

const _dtCache = {};
function getDayType(config, targetDate) {
  const target = dateOnly(targetDate);
  const anchorStr = activeAnchor(config, target);
  if (!anchorStr) return 0;
  const key = anchorStr + "|" + toDateStr(target);
  if (_dtCache[key] !== undefined) return _dtCache[key];

  const [ay, am, ad] = anchorStr.split("-").map(Number);
  const anchor = new Date(ay, am - 1, ad);
  const nDays  = config.rotation?.days?.length || 7;
  if (target.getTime() <= anchor.getTime()) { _dtCache[key] = 0; return 0; }

  let idx = 0, cur = new Date(anchor);
  while (cur < target) {
    cur = new Date(cur.getTime() + 86400000);
    if (consumesRotationDay(config, cur)) idx = (idx + 1) % nDays;
  }
  _dtCache[key] = idx;
  return idx;
}

// ─── SCHEDULE BUILDER ─────────────────────────────────────────────────────────
// Reads the config and returns the day's periods, each normalized to the shape
// the layout expects: { ...period, start:[h,m], end:[h,m], blockLetter }.
function buildSchedule(config, now) {
  if (!config) return null;
  const dow = now.getDay();
  if (dow === 5 || dow === 6) return null;

  const today = dateOnly(now);
  const ds = toDateStr(today);
  const start = config.year?.start, end = config.year?.end;
  if (start && ds < start) return null;
  if (end && ds > end) return null;

  const rotDays = config.rotation?.days || [];
  const mapPeriod = (p, dayIdx) => {
    const [sh, sm] = parseHM(p.start);
    const [eh, em] = parseHM(p.end);
    const out = { ...p, start: [sh, sm], end: [eh, em] };
    if (!out.blockLetter && p.slot != null) {
      const row = rotDays[dayIdx];
      out.blockLetter = row ? row[p.slot] : undefined;
    }
    return out;
  };

  // Overrides take precedence over no-school: an override means school IS in
  // session that day with a custom bell order. (This is what makes the first-day
  // special schedule render instead of the "off" card.)
  const ov = config.overrides?.[ds];
  if (ov && Array.isArray(ov.periods)) {
    const dayIdx = ov.rotationDay ? (ov.rotationDay - 1) : getDayType(config, now);
    return ov.periods.map((p) => mapPeriod(p, dayIdx));
  }

  if (inRanges(ds, config.noSchool)) return null;

  const template = config.weekdayTemplates?.[String(dow)];
  if (!template) return null;
  const dayIdx = getDayType(config, now);
  return template.map((p) => mapPeriod(p, dayIdx));
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const DAYS   = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_NUMBER = ["Day 1","Day 2","Day 3","Day 4","Day 5","Day 6","Day 7"];

// Preview mode (weekend / off-day testing): open the display with
//   ?preview=YYYY-MM-DD            → build that day, clock ticks from the live time
//   ?preview=YYYY-MM-DD&time=HH:MM → also start at that time of day
// This shifts the clock by a fixed offset so the previewed day advances naturally.
// With no ?preview param (the real hallway TVs) the offset is 0 → real Amman time,
// exactly as before. It only affects the one browser tab that carries the param.
const PREVIEW_OFFSET_MS = (() => {
  try {
    const p = new URLSearchParams(window.location.search);
    const d = p.get("preview");
    if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return 0;
    const [y, m, day] = d.split("-").map(Number);
    const real = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Amman" }));
    const t = p.get("time");
    let hh, mm, ss;
    if (t && /^\d{1,2}:\d{2}$/.test(t)) { [hh, mm] = t.split(":").map(Number); ss = 0; }
    else { hh = real.getHours(); mm = real.getMinutes(); ss = real.getSeconds(); }
    const target = new Date(y, m - 1, day, hh, mm, ss);
    return target.getTime() - real.getTime();
  } catch { return 0; }
})();

function getNowAmman() {
  const real = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Amman" }));
  return PREVIEW_OFFSET_MS ? new Date(real.getTime() + PREVIEW_OFFSET_MS) : real;
}
function toMins(h, m) { return h * 60 + m; }
function nowMins(d)   { return d.getHours() * 60 + d.getMinutes(); }
// 24-hour clock everywhere (HH:MM).
function fmtTime(h, m)  { return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`; }
function fmtShort(h, m) { return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`; }

function periodLetter(p) {
  if (p.blockLetter) return p.blockLetter;
  if (p.type === "advisory") return "ADV";
  if (p.type === "meeting")  return "MTG";
  if (p.type === "lunch")    return "LUN";
  if (p.type === "break")    return "BRK";
  if (p.type === "hi")       return "H/I";
  return "—";
}

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

// Pure: derive everything the render needs from a config + a moment in time.
// Shared by the live TVs (useSchedule) and the editor's WYSIWYG preview, so the
// two can never drift.
function computeSchedule(config, now) {
  const schedule = buildSchedule(config, now);
  const nm = nowMins(now), dow = now.getDay();
  let curIdx = -1, nxtIdx = -1;
  if (schedule) {
    for (let i = 0; i < schedule.length; i++) {
      const ps = toMins(schedule[i].start[0], schedule[i].start[1]);
      const pe = toMins(schedule[i].end[0],   schedule[i].end[1]);
      if (nm >= ps && nm < pe) { curIdx = i; break; }
      if (nm < ps && nxtIdx === -1) nxtIdx = i;
    }
  }

  const current = curIdx >= 0 ? schedule[curIdx] : null;
  const next    = nxtIdx >= 0 ? schedule[nxtIdx] : null;
  const minsUntilNext  = next ? toMins(next.start[0], next.start[1]) - nm : null;
  const isPassing      = !current && next && minsUntilNext !== null && minsUntilNext <= 10 && minsUntilNext > 0;
  const isBeforeSchool = !current && next && minsUntilNext !== null && minsUntilNext > 10;
  const isAfterSchool  = !current && !next && !!schedule;

  const upcoming = schedule
    ? schedule.slice(curIdx >= 0 ? curIdx + 1 : (nxtIdx >= 0 ? nxtIdx : schedule.length))
               .filter(p => toMins(p.end[0], p.end[1]) > nm)
    : [];

  const dayTypeIdx = (config && schedule) ? getDayType(config, now) : null;
  const rotRow     = (config && dayTypeIdx !== null) ? (config.rotation?.days?.[dayTypeIdx] || "") : "";
  const todayLetters = rotRow ? [...rotRow] : [];

  const hh  = String(now.getHours()).padStart(2,"0");
  const mm2 = String(now.getMinutes()).padStart(2,"0");
  const ss  = String(now.getSeconds()).padStart(2,"0");

  return { now, schedule, nm, dow, current, next, minsUntilNext, isPassing, isBeforeSchool, isAfterSchool, upcoming, dayTypeIdx, todayLetters, hh, mm2, ss };
}

function useSchedule(division) {
  const config = useConfig(division);
  const sharedMenu = useSharedMenu();
  const [now, setNow] = useState(getNowAmman);
  useEffect(() => {
    const id = setInterval(() => setNow(getNowAmman()), 1000);
    return () => clearInterval(id);
  }, []);
  return { config, sharedMenu, ...computeSchedule(config, now) };
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function BellSchedule({ division = "upperSchool" }) {
  const width = useWindowWidth();
  return width < 768 ? <MobileLayout division={division} /> : <TVLayout division={division} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// TV LAYOUT
// ═══════════════════════════════════════════════════════════════════════════════
// The 1920×1080 TV screen, rendered from already-derived state. Used both live
// (TVLayout) and in the editor preview (BellSchedulePreview) so they're identical.
function TVScreen({ division, config, sharedMenu, now, schedule, nm, dow, current, next, minsUntilNext, isPassing, isBeforeSchool, isAfterSchool, upcoming, dayTypeIdx, todayLetters, hh, mm2, ss, scale }) {
  const label = config?.meta?.displayName || (division === "middleSchool" ? "Middle School" : "Upper School");
  const year  = config?.meta?.schoolYear || "2026–2027";
  const announcement = activeAnnouncement(config, now);
  const menuSource = (config?.lunchMenu?.linked === false) ? config.lunchMenu : sharedMenu;
  const { items: menuItems, title: menuTitle } = menuForDate(menuSource, now);
  const showCategories = menuSource?.showCategories !== false;
  const showMenu = !!(schedule && !isAfterSchool && menuItems.length);
  const showFeatured = featuredVisible(config, now);

  // Event mode takes over the whole screen on any day when this division has it
  // switched on. The schedule render path below is left untouched.
  if (eventModeActive(config)) {
    const board = buildEventBoard(config, now);
    return (
      <div style={{ ...tv.screen, transform:`scale(${scale})` }}>
        <div style={tv.topBar} />
        <div style={tv.header}>
          <div style={tv.headerLeft}>
            <img src="/kings_logo.png" alt="King's Academy" style={{ height:80, width:"auto", flexShrink:0 }} />
            <div>
              <div style={tv.schoolName}>King's Academy</div>
              <div style={tv.schoolSub}>{label} · Madaba, Jordan</div>
            </div>
          </div>
          <div style={tv.headerRight}>
            <div style={tv.clock}>{hh}:{mm2}:{ss}</div>
            <div style={tv.dateStr}>{DAYS[dow]}, {MONTHS[now.getMonth()]} {now.getDate()}, {now.getFullYear()}</div>
          </div>
        </div>
        <div style={tv.divider} />
        <TVEventBoard board={board} />
        <div style={tv.divider} />
        {announcement && <TVAnnounceBar announcement={announcement} />}
        <div style={tv.footer}>
          <span style={tv.footerLabel}>{board.subtitle || board.title}</span>
          <div style={tv.footerRight}>{label} · {year}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...tv.screen, transform:`scale(${scale})` }}>
      <div style={tv.topBar} />
      <div style={tv.header}>
        <div style={tv.headerLeft}>
          <img src="/kings_logo.png" alt="King's Academy" style={{ height:80, width:"auto", flexShrink:0 }} />
          <div>
            <div style={tv.schoolName}>King's Academy</div>
            <div style={tv.schoolSub}>{label} · Academic Schedule · Madaba, Jordan</div>
          </div>
        </div>
        <div style={tv.headerRight}>
          <div style={tv.clock}>{hh}:{mm2}:{ss}</div>
          <div style={tv.dateStr}>{DAYS[dow]}, {MONTHS[now.getMonth()]} {now.getDate()}, {now.getFullYear()}</div>
        </div>
      </div>
      <div style={tv.divider} />
      <div style={tv.body}>
        <div style={tv.left}>
          {!schedule      ? <TVOffCard dow={dow} now={now} /> :
           current        ? <TVCurrentCard period={current} nm={nm} /> :
           isPassing      ? <TVPassingCard next={next} minsUntil={minsUntilNext} /> :
           isBeforeSchool ? <TVBeforeCard next={next} /> :
           isAfterSchool  ? <TVAfterCard /> :
                            <TVBeforeCard next={next} />}
          {dayTypeIdx !== null && (
            <div style={tv.dayTypeBadge}>
              <div style={tv.dayTypeNum}>{DAY_NUMBER[dayTypeIdx]}</div>
              <div style={tv.dayTypeLabel}>Today</div>
            </div>
          )}
          {showFeatured
            ? <TVFeaturedCard featured={config.featured} />
            : showMenu ? <TVMenuCard title={menuTitle} items={menuItems} showCategories={showCategories} /> : null}
        </div>
        <div style={tv.right}>
          <div style={tv.upcomingHeader}>{current ? "Coming Up" : "Today's Schedule"}</div>
          <div style={tv.upcomingList}>
            {upcoming.length === 0
              ? <div style={tv.emptyMsg}>No more periods today</div>
              : upcoming.map((p, i) => <TVUpRow key={p.id+i} period={p} isNext={i===0 && !!current} />)}
          </div>
        </div>
      </div>
      <div style={tv.divider} />
      {announcement && <TVAnnounceBar announcement={announcement} />}
      <div style={tv.footer}>
        <div style={tv.footerBlocks}>
          <span style={tv.footerLabel}>Today's Blocks</span>
          <div style={tv.chipRow}>
            {todayLetters.map((l, i) => {
              const isActive = current?.blockLetter === l && current?.type === "class";
              const isNext   = isPassing && next?.blockLetter === l;
              return <div key={i} style={{ ...tv.chip, ...(isActive ? tv.chipActive : isNext ? tv.chipNext : {}) }}>{l}</div>;
            })}
          </div>
        </div>
        <div style={tv.footerRight}>{label} · {year}</div>
      </div>
    </div>
  );
}

function TVLayout({ division }) {
  const view = useSchedule(division);
  // Scale the 1920×1080 canvas to fill whatever viewport the TV browser reports.
  // Works even on dumb smart TV browsers that ignore viewport meta tags.
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const update = () => setScale(Math.min(window.innerWidth / 1920, window.innerHeight / 1080));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return (
    <div style={{ width:"100vw", height:"100vh", overflow:"hidden", background:KA.cream }}>
      <TVScreen {...view} division={division} scale={scale} />
    </div>
  );
}

// Editor WYSIWYG preview: renders the real TV screen from an in-memory (possibly
// unpublished) config + shared menu at a chosen moment, boxed and scaled down.
export function BellSchedulePreview({ division = "upperSchool", config, sharedMenu, now, scale = 0.4 }) {
  if (!config || !now) return null;
  const view = computeSchedule(config, now);
  return (
    <div style={{ width: Math.round(1920 * scale), height: Math.round(1080 * scale), overflow: "hidden", background: KA.cream, borderRadius: 8, border: `1px solid rgba(162,145,100,0.45)`, boxShadow: "0 4px 20px rgba(35,60,95,0.15)" }}>
      <TVScreen {...view} now={now} config={config} sharedMenu={sharedMenu} division={division} scale={scale} />
    </div>
  );
}

function TVCurrentCard({ period, nm }) {
  const ps = toMins(period.start[0], period.start[1]), pe = toMins(period.end[0], period.end[1]);
  const pct = Math.min(100, Math.round(((nm-ps)/(pe-ps))*100)), left = pe-nm, ltr = periodLetter(period), dur = pe-ps;
  return (
    <div style={tv.card}>
      <div style={tv.cardStripe} />
      <div style={tv.cardInner}>
        <div style={tv.nowRow}>
          <div style={tv.nowBadge}>NOW</div>
          <div style={tv.nowTime}>{fmtTime(period.start[0],period.start[1])} – {fmtTime(period.end[0],period.end[1])}{dur>=70?" · 80 min":""}</div>
        </div>
        <div style={tv.cardMain}>
          <div style={{...tv.bigLetter, fontSize:ltr.length>2?70:130}}>{ltr}</div>
          <div>
            {(period.type==="class"||period.type==="hi") && <div style={tv.periodType}>{period.type==="class"?"Block":"H/I Block"}</div>}
            <div style={tv.periodName}>{period.label}</div>
          </div>
        </div>
        <div style={tv.remainRow}>
          <div style={tv.remainText}>{left} min{left!==1?"s":""} remaining</div>
          <div style={tv.remainPct}>{pct}%</div>
        </div>
        <div style={tv.progressTrack}><div style={{...tv.progressFill, width:`${pct}%`}} /></div>
      </div>
    </div>
  );
}

function TVPassingCard({ next, minsUntil }) {
  const ltr = periodLetter(next);
  return (
    <div style={{...tv.card, background:KA.offWhite}}>
      <div style={{...tv.cardStripe, background:KA.gold}} />
      <div style={tv.cardInner}>
        <div style={tv.nowRow}>
          <div style={{...tv.nowBadge, background:KA.gold, color:KA.navy}}>PASSING</div>
          <div style={tv.nowTime}>Next period in {minsUntil} min{minsUntil!==1?"s":""}</div>
        </div>
        <div style={tv.cardMain}>
          <div style={{...tv.bigLetter, color:KA.gold, fontSize:ltr.length>2?70:130}}>{ltr}</div>
          <div>
            <div style={{...tv.periodType, color:KA.gold}}>{next.type==="class"?"Up Next · Block":`Up Next · ${next.label}`}</div>
            <div style={tv.periodName}>{next.label}</div>
            <div style={{fontSize:20, color:"rgba(35,60,95,0.55)", marginTop:6}}>Starts at {fmtTime(next.start[0],next.start[1])}</div>
          </div>
        </div>
        <div style={tv.passingBar}><div style={{...tv.passingFill, width:`${Math.round((1-minsUntil/10)*100)}%`}} /></div>
      </div>
    </div>
  );
}

function TVBeforeCard({ next }) {
  return (
    <div style={{...tv.card, background:KA.offWhite}}>
      <div style={{...tv.cardStripe, background:KA.navy}} />
      <div style={tv.cardInner}>
        <div style={{...tv.nowBadge, background:KA.navy, color:KA.white, marginBottom:16, display:"inline-block"}}>SCHOOL BEGINS SOON</div>
        <div style={{fontSize:22, color:KA.navy, opacity:0.6}}>First period at {next?fmtTime(next.start[0],next.start[1]):"—"}</div>
      </div>
    </div>
  );
}

function TVAfterCard() {
  return (
    <div style={{...tv.card, background:KA.offWhite}}>
      <div style={{...tv.cardStripe, background:KA.navy}} />
      <div style={tv.cardInner}>
        <div style={{...tv.nowBadge, background:KA.navy, color:KA.white, marginBottom:16, display:"inline-block"}}>SCHOOL DAY COMPLETE</div>
        <div style={{fontSize:22, color:KA.navy, opacity:0.5}}>See you tomorrow.</div>
      </div>
    </div>
  );
}

function TVOffCard({ dow, now }) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const schoolStart = new Date(2026, 7, 17);
  let msg, sub;
  if (today < schoolStart) { msg="School Year Begins Aug 17"; sub="2026–2027 · A–G Rotating Block"; }
  else if (dow===5||dow===6) { msg="Weekend"; sub="Enjoy your time off."; }
  else { msg="No Classes Today"; sub="Enjoy your time off."; }
  return (
    <div style={{...tv.card, background:KA.offWhite}}>
      <div style={{...tv.cardStripe, background:KA.gold}} />
      <div style={tv.cardInner}>
        <div style={{...tv.nowBadge, background:KA.gold, color:KA.navy, marginBottom:16, display:"inline-block"}}>{msg.toUpperCase()}</div>
        <div style={{fontSize:22, color:KA.navy, opacity:0.5}}>{sub}</div>
      </div>
    </div>
  );
}

function TVUpRow({ period, isNext }) {
  const ltr = periodLetter(period);
  const dur = toMins(period.end[0],period.end[1]) - toMins(period.start[0],period.start[1]);
  const isClass = period.type==="class";
  const bgColor = isClass?KA.red:period.type==="meeting"?KA.navy:period.type==="lunch"?KA.gold:period.type==="hi"?KA.navyDark:"rgba(35,60,95,0.1)";
  const txtColor = isClass||period.type==="meeting"||period.type==="hi"?KA.white:KA.navy;
  return (
    <div style={{...tv.upRow, ...(isNext?tv.upRowNext:{})}}>
      <div style={tv.upTime}>{fmtShort(period.start[0],period.start[1])}<span style={{color:"rgba(35,60,95,0.25)"}}> – </span>{fmtShort(period.end[0],period.end[1])}</div>
      <div style={{...tv.upLetter, background:bgColor, color:txtColor, fontSize:ltr.length>2?12:17}}>{ltr}</div>
      <div style={tv.upName}>{period.label}{dur>=70?<span style={{fontSize:14,opacity:0.45,marginLeft:8}}>80 min</span>:null}</div>
      <div style={tv.upDur}>{dur}m</div>
      {isNext&&<div style={tv.upNextTag}>NEXT</div>}
    </div>
  );
}

// Lunch menu card. Items are split across TWO columns so ~4 fit at full size
// before the auto-fit scaler kicks in (the TV can't scroll). Categories can be
// hidden, leaving plain dish lines under the LUNCH header. The scaler measures
// once at natural size and re-fits whenever the available height changes.
function TVMenuCard({ title, items, showCategories }) {
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const [scale, setScale] = useState(1);

  const half = Math.ceil(items.length / 2);
  const columns = [items.slice(0, half), items.slice(half)];

  const sig = items.map((it) => it.text).join("|") + "|" + showCategories; // re-measure when content changes
  useLayoutEffect(() => { setScale(1); }, [sig]);
  useLayoutEffect(() => {
    if (scale !== 1) return;
    const wrap = wrapRef.current, list = listRef.current;
    if (!wrap || !list) return;
    const avail = wrap.clientHeight, needed = list.scrollHeight;
    if (needed > avail && needed > 0) setScale(Math.max(0.4, avail / needed));
  }, [scale, sig]);
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setScale(1));
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  return (
    <div style={tv.menuCard}>
      <div style={tv.menuHeader}>{title || "Today's Lunch"}</div>
      <div ref={wrapRef} style={tv.menuFit}>
        <div ref={listRef} style={{ ...tv.menuColumns, transform: `scale(${scale})`, transformOrigin: "top left", width: `${100 / scale}%` }}>
          {columns.map((col, ci) => (
            <div key={ci} style={tv.menuColumn}>
              {col.map((it, i) => (
                <div key={i} style={showCategories ? tv.menuRow : tv.menuRowPlain}>
                  {showCategories && <div style={tv.menuCat}>{it.category}</div>}
                  <div style={tv.menuItem}>{it.text}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Bottom announcement bar. A long announcement at full size would grow tall and
// push the body up, crushing the cards above it (title/bio squeezed off). So we
// cap the bar's height and shrink the font to fit within it. Short announcements
// are untouched (they stay at full size).
function TVAnnounceBar({ announcement }) {
  const ref = useRef(null);
  const BASE = 40, FLOOR = 22, MAX_H = 100; // ~2 lines at full size, then it shrinks
  const [fs, setFs] = useState(BASE);
  const sig = announcement.html || "";
  useLayoutEffect(() => { setFs(BASE); }, [sig]);
  useLayoutEffect(() => {
    if (fs !== BASE) return; // measure once at full size, then apply the shrink
    const el = ref.current;
    if (!el) return;
    if (el.scrollHeight > MAX_H) setFs(Math.max(FLOOR, Math.floor(BASE * MAX_H / el.scrollHeight)));
  }, [fs, sig]);
  return (
    <div style={{ ...tv.announceBar, ...announceStyle(announcement.style) }}>
      <div ref={ref} style={{ ...tv.announceText, fontSize: fs, maxHeight: MAX_H, overflow: "hidden" }}
        dangerouslySetInnerHTML={{ __html: announcement.html }} />
    </div>
  );
}

// Featured speaker card — photo on the LEFT, text on the RIGHT. The slot height
// varies with how much room the schedule card above leaves, so we measure the
// available height and shrink the FONT + PHOTO sizes (real sizes, not a CSS
// transform) until the content fits. Because the text REFLOWS as it shrinks, the
// height is non-linear in the scale factor — a single proportional guess can leave
// the last line clipped. So instead of measuring once, we step the factor down and
// re-measure until it actually fits (or hits a readable floor). Each step strictly
// decreases the factor, so it always terminates, and because it runs in a layout
// effect the viewer only ever sees the final fitted size.
function TVFeaturedCard({ featured }) {
  const wrapRef = useRef(null);
  const contentRef = useRef(null);
  const [factor, setFactor] = useState(1);
  const f = featured || {};
  const sig = `${f.eyebrow || ""}|${f.name || ""}|${f.bio || ""}|${f.photo ? f.photo.length : 0}`;

  const MIN_FACTOR = 0.3; // readability floor; was effectively 0.5, which clipped long bios in short slots

  useLayoutEffect(() => { setFactor(1); }, [sig]); // new content → start at full size, then fit down
  useLayoutEffect(() => {
    const wrap = wrapRef.current, content = contentRef.current;
    if (!wrap || !content) return;
    const avail = wrap.clientHeight;
    if (avail <= 0) return;
    const needed = content.scrollHeight;
    if (needed <= avail + 1) return;   // fits — done
    if (factor <= MIN_FACTOR) return;  // already as small as we allow
    // Proportional estimate for speed, but cap the step so we approach from above
    // and re-check. Reflow makes the true height shrink faster than linear, so this
    // converges in a pass or two without overshooting into needlessly tiny text.
    const estimate = factor * (avail / needed);
    const next = Math.max(MIN_FACTOR, Math.min(factor - 0.02, estimate));
    if (next < factor) setFactor(next);
  }, [factor, sig]);
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setFactor(1)); // slot resized → re-fit from full size
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  const ph = Math.round(300 * factor);
  return (
    <div style={tv.featCard}>
      <div ref={wrapRef} style={tv.featFit}>
        <div ref={contentRef} style={{ ...tv.featInner, gap: Math.round(40 * factor) }}>
          {f.photo && (
            <div style={{ ...tv.featPhotoWrap, width: ph, height: ph }}>
              <img src={f.photo} alt={f.name || "Speaker"} style={tv.featPhoto} onLoad={() => setFactor(1)} />
            </div>
          )}
          <div style={tv.featText}>
            <div style={{ ...tv.featEyebrow, fontSize: Math.round(18 * factor), marginBottom: Math.round(16 * factor) }}>{f.eyebrow || "Upcoming Speaker"}</div>
            {f.name && <div style={{ ...tv.featName, fontSize: Math.round(52 * factor), marginBottom: Math.round(18 * factor) }}>{f.name}</div>}
            {f.bio && <div style={{ ...tv.featBio, fontSize: Math.round(30 * factor) }}>{f.bio}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── EVENT BOARD (TV) ─────────────────────────────────────────────────────────
// Fills the body in event mode: an optional flyer on the left and/or the agenda
// list on the right (flyer alone fills the width when there are no events).
function TVEventBoard({ board }) {
  const hasPoster = !!board.poster;
  const hasEvents = board.events.length > 0;
  return (
    <div style={evTV.board}>
      <div style={evTV.header}>
        <div style={evTV.title}>{board.title}</div>
        {board.subtitle && <div style={evTV.sub}>{board.subtitle}</div>}
      </div>
      <div style={evTV.body}>
        {hasPoster && (
          <div style={{ ...evTV.posterWrap, ...(hasEvents ? {} : evTV.posterWrapFull) }}>
            <img src={board.poster} alt={board.title} style={{ ...evTV.poster, objectFit: board.posterFit }} />
          </div>
        )}
        {hasEvents && (
          <div style={{ ...evTV.listCol, ...(hasPoster ? {} : evTV.listColFull) }}>
            <TVEventList events={board.events} />
          </div>
        )}
      </div>
    </div>
  );
}

// Top-aligned auto-fit (same transform approach as the lunch menu) so a long list
// never scrolls on a TV. Re-fits when the content or the slot changes.
function TVEventList({ events }) {
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const [scale, setScale] = useState(1);
  const sig = events.map((e) => e.id + e.status).join("|");

  useLayoutEffect(() => { setScale(1); }, [sig]);
  useLayoutEffect(() => {
    if (scale !== 1) return;
    const wrap = wrapRef.current, list = listRef.current;
    if (!wrap || !list) return;
    const avail = wrap.clientHeight, needed = list.scrollHeight;
    if (needed > avail && needed > 0) setScale(Math.max(0.35, avail / needed));
  }, [scale, sig]);
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setScale(1));
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapRef} style={evTV.fit}>
      <div ref={listRef} style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: `${100 / scale}%` }}>
        {events.map((ev) => {
          const badge = ev.status === "now" ? "NOW" : ev.status === "next" ? "NEXT" : "";
          return (
            <div key={ev.id} style={{ ...evTV.row, ...(ev.status === "now" ? evTV.rowNow : ev.status === "past" ? evTV.rowPast : {}) }}>
              <div style={evTV.time}>
                {ev.timeLabel || "—"}
                {ev.dateLabel && <div style={evTV.date}>{ev.dateLabel}</div>}
              </div>
              <div style={evTV.main}>
                <div style={evTV.evTitle}>{ev.title}</div>
                {(ev.location || ev.note) && (
                  <div style={evTV.meta}>
                    {ev.location && <span style={evTV.loc}>{ev.location}</span>}
                    {ev.location && ev.note && <span style={evTV.dot}> · </span>}
                    {ev.note && <span style={evTV.note}>{ev.note}</span>}
                  </div>
                )}
              </div>
              {badge && <div style={{ ...evTV.badge, ...(badge === "NOW" ? evTV.badgeNow : evTV.badgeNext) }}>{badge}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MOBILE LAYOUT
// ═══════════════════════════════════════════════════════════════════════════════
function MobileLayout({ division }) {
  const { now, config, sharedMenu, schedule, nm, dow, current, next, minsUntilNext, isPassing, isBeforeSchool, isAfterSchool, upcoming, dayTypeIdx, todayLetters, hh, mm2, ss } = useSchedule(division);
  const announcement = activeAnnouncement(config, now);

  // Event mode takes over on mobile too. Header + announcement bar + events board,
  // nothing else; the schedule render path below is left untouched.
  if (eventModeActive(config)) {
    const eventBoard = buildEventBoard(config, now);
    return (
      <div style={mob.screen}>
        <div style={mob.header}>
          <div style={mob.headerTop}>
            <img src="/kings_logo.png" alt="King's Academy" style={{height:40,width:"auto"}} />
            <div style={mob.schoolName}>King's Academy</div>
          </div>
          <div style={mob.clockRow}>
            <div style={mob.clock}>{hh}:{mm2}:{ss}</div>
            <div style={mob.dateStr}>{DAYS[dow]}, {MONTHS[now.getMonth()]} {now.getDate()}</div>
          </div>
        </div>
        <div style={mob.topBar} />
        {announcement && (
          <div style={{ ...mob.announceBar, ...announceStyle(announcement.style) }} dangerouslySetInnerHTML={{ __html: announcement.html }} />
        )}
        <MobEventBoard board={eventBoard} />
      </div>
    );
  }

  const menuSource = (config?.lunchMenu?.linked === false) ? config.lunchMenu : sharedMenu;
  const { items: menuItems, title: menuTitle } = menuForDate(menuSource, now);
  const showCategories = menuSource?.showCategories !== false;
  const showMenu = !!(schedule && !isAfterSchool && menuItems.length);
  const showFeatured = featuredVisible(config, now);
  return (
    <div style={mob.screen}>
      <div style={mob.header}>
        <div style={mob.headerTop}>
          <img src="/kings_logo.png" alt="King's Academy" style={{height:40,width:"auto"}} />
          <div style={mob.schoolName}>King's Academy</div>
        </div>
        <div style={mob.clockRow}>
          <div style={mob.clock}>{hh}:{mm2}:{ss}</div>
          <div style={mob.dateStr}>{DAYS[dow]}, {MONTHS[now.getMonth()]} {now.getDate()}</div>
        </div>
      </div>
      <div style={mob.topBar} />
      <div style={mob.hero}>
        {!schedule      ? <MobOffCard dow={dow} now={now} /> :
         current        ? <MobCurrentCard period={current} nm={nm} /> :
         isPassing      ? <MobPassingCard next={next} minsUntil={minsUntilNext} /> :
         isBeforeSchool ? <MobStatusCard label="School Begins Soon" sub={`First period at ${next?fmtTime(next.start[0],next.start[1]):"—"}`} color={KA.navy} /> :
         isAfterSchool  ? <MobStatusCard label="School Day Complete" sub="See you tomorrow." color={KA.navy} /> :
                          <MobStatusCard label="School Begins Soon" sub={`First period at ${next?fmtTime(next.start[0],next.start[1]):"—"}`} color={KA.navy} />}
      </div>
      {announcement && (
        <div style={{ ...mob.announceBar, ...announceStyle(announcement.style) }} dangerouslySetInnerHTML={{ __html: announcement.html }} />
      )}
      {dayTypeIdx !== null && (
        <div style={mob.dayRow}>
          <div style={mob.dayBadge}><span style={mob.dayLetter}>{DAY_NUMBER[dayTypeIdx]}</span></div>
          <div style={mob.chips}>
            {todayLetters.map((l, i) => {
              const isActive = current?.blockLetter===l && current?.type==="class";
              const isNextUp = isPassing && next?.blockLetter===l;
              return <div key={i} style={{...mob.chip,...(isActive?mob.chipActive:isNextUp?mob.chipNext:{})}}>{l}</div>;
            })}
          </div>
        </div>
      )}
      {schedule && (
        <div style={mob.list}>
          <div style={mob.listHeader}>Today's Schedule</div>
          {schedule.map((p, i) => {
            const isCur=current?.id===p.id, isNxt=next?.id===p.id;
            const ltr=periodLetter(p), isClass=p.type==="class";
            const past=toMins(p.end[0],p.end[1])<=nm;
            const dur=toMins(p.end[0],p.end[1])-toMins(p.start[0],p.start[1]);
            const bgColor=isClass?KA.red:p.type==="meeting"?KA.navy:p.type==="lunch"?KA.gold:p.type==="hi"?KA.navyDark:"rgba(35,60,95,0.1)";
            const txtColor=isClass||p.type==="meeting"||p.type==="hi"?KA.white:KA.navy;
            return (
              <div key={p.id+i} style={{...mob.listRow,...(isCur?mob.listRowCur:isNxt?mob.listRowNext:past?mob.listRowPast:{})}}>
                <div style={mob.listTime}>{fmtShort(p.start[0],p.start[1])}</div>
                <div style={{...mob.listPill, background:bgColor, color:txtColor, fontSize:ltr.length>2?9:11}}>{ltr}</div>
                <div style={mob.listName}>{p.label}{dur>=70?<span style={{fontSize:10,opacity:0.5}}> · 80m</span>:null}</div>
                {isCur&&<div style={mob.listNowTag}>NOW</div>}
                {isNxt&&!isCur&&<div style={mob.listNextTag}>NEXT</div>}
              </div>
            );
          })}
        </div>
      )}
      {showFeatured && (
        <div style={mob.featCard}>
          <div style={mob.featEyebrow}>{config.featured.eyebrow || "Upcoming Speaker"}</div>
          {config.featured.photo && <img src={config.featured.photo} alt={config.featured.name || "Speaker"} style={mob.featPhoto} />}
          {config.featured.name && <div style={mob.featName}>{config.featured.name}</div>}
          {config.featured.bio && <div style={mob.featBio}>{config.featured.bio}</div>}
        </div>
      )}
      {showMenu && !showFeatured && (
        <div style={mob.menuCard}>
          <div style={mob.menuHeader}>{menuTitle || "Today's Lunch"}</div>
          {menuItems.map((it, i) => (
            <div key={i} style={mob.menuRow}>
              {showCategories && <span style={mob.menuCat}>{it.category}</span>}
              <span style={mob.menuItem}>{it.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── EVENT BOARD (Mobile) ─────────────────────────────────────────────────────
function MobEventBoard({ board }) {
  return (
    <div style={evMob.wrap}>
      <div style={evMob.head}>
        <div style={evMob.title}>{board.title}</div>
        {board.subtitle && <div style={evMob.sub}>{board.subtitle}</div>}
      </div>
      {board.poster && <img src={board.poster} alt={board.title} style={{ ...evMob.poster, objectFit: board.posterFit }} />}
      {board.events.map((ev) => {
        const badge = ev.status === "now" ? "NOW" : ev.status === "next" ? "NEXT" : "";
        return (
          <div key={ev.id} style={{ ...evMob.row, ...(ev.status === "now" ? evMob.rowNow : ev.status === "past" ? evMob.rowPast : {}) }}>
            <div style={evMob.rowTop}>
              <span style={evMob.time}>{ev.timeLabel || "—"}</span>
              {ev.dateLabel && <span style={evMob.date}>{ev.dateLabel}</span>}
              {badge && <span style={{ ...evMob.badge, ...(badge === "NOW" ? evMob.badgeNow : evMob.badgeNext) }}>{badge}</span>}
            </div>
            <div style={evMob.name}>{ev.title}</div>
            {(ev.location || ev.note) && (
              <div style={evMob.meta}>
                {ev.location && <span style={evMob.loc}>{ev.location}</span>}
                {ev.location && ev.note && " · "}
                {ev.note && <span style={evMob.note}>{ev.note}</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MobCurrentCard({ period, nm }) {
  const ps=toMins(period.start[0],period.start[1]), pe=toMins(period.end[0],period.end[1]);
  const pct=Math.min(100,Math.round(((nm-ps)/(pe-ps))*100)), left=pe-nm, ltr=periodLetter(period);
  return (
    <div style={mob.currentCard}>
      <div style={mob.currentTop}>
        <div style={mob.nowBadge}>NOW</div>
        <div style={mob.currentTime}>{fmtTime(period.start[0],period.start[1])} – {fmtTime(period.end[0],period.end[1])}</div>
      </div>
      <div style={mob.currentBody}>
        <div style={{...mob.currentLetter, fontSize:ltr.length>2?44:64}}>{ltr}</div>
        <div>
          <div style={mob.currentType}>{period.type==="class"?"Block":period.label}</div>
          <div style={mob.currentName}>{period.label}</div>
        </div>
      </div>
      <div style={mob.remainRow}>
        <span style={mob.remainText}>{left} min{left!==1?"s":""} remaining</span>
        <span style={mob.remainPct}>{pct}%</span>
      </div>
      <div style={mob.progressTrack}><div style={{...mob.progressFill, width:`${pct}%`}} /></div>
    </div>
  );
}

function MobPassingCard({ next, minsUntil }) {
  const ltr=periodLetter(next);
  return (
    <div style={{...mob.currentCard, borderLeftColor:KA.gold}}>
      <div style={mob.currentTop}>
        <div style={{...mob.nowBadge, background:KA.gold, color:KA.navy}}>PASSING</div>
        <div style={mob.currentTime}>Next in {minsUntil} min{minsUntil!==1?"s":""}</div>
      </div>
      <div style={mob.currentBody}>
        <div style={{...mob.currentLetter, color:KA.gold, fontSize:ltr.length>2?44:64}}>{ltr}</div>
        <div>
          <div style={{...mob.currentType, color:KA.gold}}>Up Next</div>
          <div style={mob.currentName}>{next.label}</div>
          <div style={{fontSize:13, color:"rgba(35,60,95,0.5)", marginTop:2}}>Starts at {fmtTime(next.start[0],next.start[1])}</div>
        </div>
      </div>
      <div style={mob.progressTrack}><div style={{...mob.progressFill, width:`${Math.round((1-minsUntil/10)*100)}%`, background:KA.gold}} /></div>
    </div>
  );
}

function MobStatusCard({ label, sub, color }) {
  return (
    <div style={{...mob.currentCard, borderLeftColor:color}}>
      <div style={{...mob.nowBadge, background:color, color:KA.white, marginBottom:10, display:"inline-block"}}>{label.toUpperCase()}</div>
      <div style={{fontSize:14, color:KA.navy, opacity:0.6}}>{sub}</div>
    </div>
  );
}

function MobOffCard({ dow, now }) {
  const today=new Date(now.getFullYear(),now.getMonth(),now.getDate());
  const schoolStart=new Date(2026,7,17);
  let msg,sub;
  if(today<schoolStart){msg="School Year Begins Aug 17";sub="2026–2027 · A–G Rotating Block";}
  else if(dow===5||dow===6){msg="Weekend";sub="Enjoy your time off.";}
  else{msg="No Classes Today";sub="Enjoy your time off.";}
  return (
    <div style={{...mob.currentCard, borderLeftColor:KA.gold}}>
      <div style={{...mob.nowBadge, background:KA.gold, color:KA.navy, marginBottom:10, display:"inline-block"}}>{msg.toUpperCase()}</div>
      <div style={{fontSize:14, color:KA.navy, opacity:0.5}}>{sub}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TV STYLES
// ═══════════════════════════════════════════════════════════════════════════════
const tv = {
  screen:{width:"1920px",height:"1080px",background:KA.cream,color:KA.navy,fontFamily:"'Helvetica Neue',Helvetica,Arial,sans-serif",display:"flex",flexDirection:"column",boxSizing:"border-box",isolation:"isolate",transformOrigin:"top left",overflow:"hidden"},
  topBar:{height:10,background:KA.red,flexShrink:0},
  header:{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"22px 48px 20px",background:KA.navy},
  headerLeft:{display:"flex",alignItems:"center",gap:24},
  headerRight:{textAlign:"right"},
  schoolName:{fontSize:46,fontWeight:300,letterSpacing:6,color:KA.white,textTransform:"uppercase",fontFamily:"Georgia,'Times New Roman',serif"},
  schoolSub:{fontSize:16,letterSpacing:3,color:KA.goldLight,textTransform:"uppercase",marginTop:6},
  clock:{fontFamily:"'Helvetica Neue',monospace",fontSize:80,fontWeight:200,color:KA.white,letterSpacing:3,lineHeight:1},
  dateStr:{fontSize:18,color:KA.goldLight,letterSpacing:2,marginTop:6},
  divider:{height:2,background:KA.gold,opacity:0.4,flexShrink:0},
  body:{flex:1,display:"flex",minHeight:0},
  left:{width:"52%",minHeight:0,padding:"36px 44px",display:"flex",flexDirection:"column",gap:28,borderRight:`1px solid rgba(162,145,100,0.2)`,overflow:"hidden"},
  right:{flex:1,padding:"36px 44px",display:"flex",flexDirection:"column",overflow:"hidden"},
  card:{background:KA.white,borderRadius:6,position:"relative",overflow:"hidden",boxShadow:"0 2px 24px rgba(35,60,95,0.10)",border:`1px solid rgba(162,145,100,0.2)`},
  cardStripe:{position:"absolute",left:0,top:0,bottom:0,width:8,background:KA.red},
  cardInner:{padding:"28px 28px 28px 44px"},
  nowRow:{display:"flex",alignItems:"center",gap:16,marginBottom:22},
  nowBadge:{background:KA.red,color:KA.white,fontSize:14,fontWeight:700,letterSpacing:3,padding:"6px 16px",borderRadius:3},
  nowTime:{fontSize:20,color:"rgba(35,60,95,0.55)"},
  cardMain:{display:"flex",alignItems:"center",gap:28,marginBottom:28},
  bigLetter:{fontSize:130,fontWeight:700,lineHeight:1,color:KA.red,fontFamily:"Georgia,'Times New Roman',serif"},
  periodType:{fontSize:15,fontWeight:600,letterSpacing:3,textTransform:"uppercase",color:KA.red,marginBottom:6},
  periodName:{fontSize:40,fontWeight:300,color:KA.navy,fontFamily:"Georgia,'Times New Roman',serif"},
  remainRow:{display:"flex",justifyContent:"space-between",marginBottom:12},
  remainText:{fontSize:20,color:"rgba(35,60,95,0.6)"},
  remainPct:{fontSize:18,color:"rgba(35,60,95,0.4)",fontWeight:500},
  progressTrack:{height:5,background:"rgba(35,60,95,0.1)",borderRadius:3},
  progressFill:{height:"100%",background:KA.red,borderRadius:3,transition:"width 30s linear"},
  passingBar:{height:5,background:`rgba(162,145,100,0.2)`,borderRadius:3,marginTop:12},
  passingFill:{height:"100%",background:KA.gold,borderRadius:3,transition:"width 10s linear"},
  dayTypeBadge:{display:"flex",flexDirection:"column",alignItems:"center",background:KA.navy,borderRadius:6,padding:"20px 32px",border:`1px solid rgba(162,145,100,0.2)`},
  dayTypeNum:{fontSize:36,fontWeight:700,color:KA.gold,lineHeight:1,fontFamily:"Georgia,'Times New Roman',serif"},
  dayTypeLabel:{fontSize:14,letterSpacing:3,color:KA.goldLight,textTransform:"uppercase",marginTop:6},
  upcomingHeader:{fontSize:14,fontWeight:700,letterSpacing:3.5,textTransform:"uppercase",color:KA.red,marginBottom:20,paddingBottom:12,borderBottom:`1px solid rgba(162,145,100,0.25)`},
  upcomingList:{display:"flex",flexDirection:"column",gap:2,overflowY:"auto"},
  emptyMsg:{fontSize:20,color:"rgba(35,60,95,0.4)",fontStyle:"italic"},
  upRow:{display:"flex",alignItems:"center",gap:16,padding:"14px 18px",borderRadius:4,background:"rgba(255,255,255,0.6)",borderBottom:`1px solid rgba(162,145,100,0.12)`},
  upRowNext:{background:KA.white,boxShadow:"0 1px 8px rgba(35,60,95,0.08)",borderLeft:`5px solid ${KA.red}`,paddingLeft:13},
  upTime:{fontFamily:"monospace",fontSize:18,color:"rgba(35,60,95,0.45)",width:150,flexShrink:0},
  upLetter:{fontSize:17,fontWeight:700,padding:"5px 8px",borderRadius:3,minWidth:58,textAlign:"center",flexShrink:0,fontFamily:"Georgia,serif"},
  upName:{fontSize:19,color:KA.navy,flex:1,fontWeight:300},
  upDur:{fontSize:16,color:"rgba(35,60,95,0.35)",whiteSpace:"nowrap"},
  upNextTag:{fontSize:13,fontWeight:700,letterSpacing:2,color:KA.red},
  footer:{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 48px",background:KA.navy},
  footerBlocks:{display:"flex",alignItems:"center",gap:18},
  footerLabel:{fontSize:13,letterSpacing:2.5,color:KA.goldLight,textTransform:"uppercase"},
  chipRow:{display:"flex",gap:6},
  chip:{fontSize:16,fontWeight:600,letterSpacing:1.5,padding:"5px 14px",borderRadius:3,color:"rgba(255,255,255,0.3)",border:"1px solid rgba(255,255,255,0.1)",fontFamily:"Georgia,serif"},
  chipActive:{background:KA.red,color:KA.white,border:`1px solid ${KA.red}`},
  chipNext:{background:"rgba(162,145,100,0.25)",color:KA.goldLight,border:`1px solid rgba(162,145,100,0.4)`},
  footerRight:{fontSize:13,letterSpacing:2.5,color:KA.goldLight,textTransform:"uppercase"},
  announceBar:{display:"flex",alignItems:"center",justifyContent:"center",padding:"22px 48px",flexShrink:0},
  announceText:{fontSize:40,fontWeight:600,textAlign:"center",lineHeight:1.2,fontFamily:"Georgia,'Times New Roman',serif"},
  menuCard:{flex:1,minHeight:0,display:"flex",flexDirection:"column",background:KA.white,borderRadius:6,border:`1px solid rgba(162,145,100,0.25)`,boxShadow:"0 2px 16px rgba(35,60,95,0.08)",overflow:"hidden"},
  menuHeader:{fontSize:16,fontWeight:700,letterSpacing:3.5,textTransform:"uppercase",color:KA.red,padding:"18px 24px 14px",borderBottom:`1px solid rgba(162,145,100,0.25)`,flexShrink:0},
  menuFit:{flex:1,minHeight:0,overflow:"hidden"},
  menuColumns:{display:"flex",flexDirection:"row",gap:28,padding:"6px 24px 18px",alignItems:"flex-start"},
  menuColumn:{flex:1,minWidth:0,display:"flex",flexDirection:"column"},
  menuRow:{display:"flex",alignItems:"baseline",gap:12,padding:"10px 0",borderBottom:`1px solid rgba(162,145,100,0.12)`},
  menuRowPlain:{padding:"10px 0",borderBottom:`1px solid rgba(162,145,100,0.12)`},
  menuCat:{fontSize:13,fontWeight:600,letterSpacing:1.5,textTransform:"uppercase",color:KA.gold,width:108,flexShrink:0},
  menuItem:{fontSize:24,color:KA.navy,fontWeight:300,fontFamily:"Georgia,'Times New Roman',serif",flex:1,minWidth:0},
  featCard:{flex:1,minHeight:0,display:"flex",flexDirection:"column",background:KA.white,borderRadius:6,border:`1px solid rgba(162,145,100,0.25)`,boxShadow:"0 2px 16px rgba(35,60,95,0.08)",padding:"28px 36px",overflow:"hidden"},
  featFit:{flex:1,minHeight:0,width:"100%",overflow:"hidden",display:"flex",alignItems:"center"},
  featInner:{width:"100%",display:"flex",flexDirection:"row",alignItems:"center",textAlign:"left"},
  featPhotoWrap:{borderRadius:8,overflow:"hidden",flexShrink:0,border:`3px solid ${KA.gold}`,boxShadow:"0 4px 16px rgba(35,60,95,0.18)"},
  featPhoto:{width:"100%",height:"100%",objectFit:"cover",display:"block"},
  featText:{flex:1,minWidth:0,display:"flex",flexDirection:"column",textAlign:"left"},
  featEyebrow:{fontWeight:700,letterSpacing:3,textTransform:"uppercase",color:KA.red,lineHeight:1.25},
  featName:{fontWeight:400,color:KA.navy,fontFamily:"Georgia,'Times New Roman',serif",lineHeight:1.08},
  featBio:{color:"rgba(35,60,95,0.8)",fontWeight:300,lineHeight:1.4,whiteSpace:"pre-line"},
};

// ─── EVENT BOARD STYLES (TV) ───────────────────────────────────────────────────
const evTV = {
  board:{flex:1,minHeight:0,display:"flex",flexDirection:"column",overflow:"hidden"},
  header:{padding:"26px 48px 18px",borderBottom:`1px solid rgba(162,145,100,0.25)`,flexShrink:0},
  title:{fontSize:54,fontWeight:400,color:KA.navy,fontFamily:"Georgia,'Times New Roman',serif",lineHeight:1.03},
  sub:{fontSize:20,letterSpacing:3,textTransform:"uppercase",color:KA.gold,fontWeight:600,marginTop:10},
  body:{flex:1,minHeight:0,display:"flex"},
  posterWrap:{width:"46%",minHeight:0,display:"flex",alignItems:"center",justifyContent:"center",padding:"28px 36px",borderRight:`1px solid rgba(162,145,100,0.2)`,overflow:"hidden"},
  posterWrapFull:{width:"100%",borderRight:"none"},
  poster:{maxWidth:"100%",maxHeight:"100%",width:"auto",height:"auto",borderRadius:8,boxShadow:"0 4px 24px rgba(35,60,95,0.18)",border:`1px solid rgba(162,145,100,0.25)`},
  listCol:{flex:1,minHeight:0,padding:"22px 44px",overflow:"hidden",display:"flex",flexDirection:"column"},
  listColFull:{padding:"26px 72px"},
  fit:{flex:1,minHeight:0,overflow:"hidden"},
  row:{display:"flex",alignItems:"flex-start",gap:28,padding:"18px 10px",borderBottom:`1px solid rgba(162,145,100,0.18)`},
  rowNow:{background:KA.white,borderRadius:6,boxShadow:"0 2px 14px rgba(35,60,95,0.10)",borderLeft:`6px solid ${KA.red}`,paddingLeft:20},
  rowPast:{opacity:0.4},
  time:{width:240,flexShrink:0,fontSize:28,fontWeight:600,color:KA.navy,fontFamily:"monospace",lineHeight:1.15},
  date:{fontSize:16,fontWeight:600,letterSpacing:1.5,textTransform:"uppercase",color:KA.gold,marginTop:4,fontFamily:"'Helvetica Neue',Helvetica,Arial,sans-serif"},
  main:{flex:1,minWidth:0},
  evTitle:{fontSize:32,fontWeight:300,color:KA.navy,fontFamily:"Georgia,'Times New Roman',serif",lineHeight:1.15},
  meta:{fontSize:20,color:"rgba(35,60,95,0.62)",marginTop:6},
  loc:{fontWeight:600,color:KA.gold},
  dot:{color:"rgba(35,60,95,0.3)"},
  note:{fontStyle:"italic"},
  badge:{flexShrink:0,alignSelf:"center",fontSize:14,fontWeight:700,letterSpacing:2,padding:"6px 14px",borderRadius:3},
  badgeNow:{background:KA.red,color:KA.white},
  badgeNext:{background:"rgba(162,145,100,0.22)",color:KA.navy,border:`1px solid rgba(162,145,100,0.5)`},
};

// ═══════════════════════════════════════════════════════════════════════════════
// MOBILE STYLES
// ═══════════════════════════════════════════════════════════════════════════════
const mob = {
  screen:{minHeight:"100vh",background:KA.cream,color:KA.navy,fontFamily:"'Helvetica Neue',Helvetica,Arial,sans-serif",display:"flex",flexDirection:"column",boxSizing:"border-box"},
  header:{background:KA.navy,padding:"14px 16px 12px"},
  headerTop:{display:"flex",alignItems:"center",gap:10,marginBottom:10},
  schoolName:{fontSize:18,fontWeight:300,letterSpacing:3,color:KA.white,textTransform:"uppercase",fontFamily:"Georgia,serif"},
  clockRow:{display:"flex",justifyContent:"space-between",alignItems:"baseline"},
  clock:{fontFamily:"monospace",fontSize:36,fontWeight:200,color:KA.white,letterSpacing:2,lineHeight:1},
  dateStr:{fontSize:12,color:KA.goldLight,letterSpacing:1},
  topBar:{height:4,background:KA.red,flexShrink:0},
  hero:{padding:"16px 16px 0"},
  currentCard:{background:KA.white,borderRadius:8,borderLeft:`5px solid ${KA.red}`,padding:"16px 16px 14px",boxShadow:"0 2px 12px rgba(35,60,95,0.10)"},
  currentTop:{display:"flex",alignItems:"center",gap:10,marginBottom:12},
  nowBadge:{background:KA.red,color:KA.white,fontSize:10,fontWeight:700,letterSpacing:2,padding:"3px 10px",borderRadius:2},
  currentTime:{fontSize:13,color:"rgba(35,60,95,0.55)"},
  currentBody:{display:"flex",alignItems:"center",gap:16,marginBottom:14},
  currentLetter:{fontSize:64,fontWeight:700,lineHeight:1,color:KA.red,fontFamily:"Georgia,serif"},
  currentType:{fontSize:10,fontWeight:700,letterSpacing:2,textTransform:"uppercase",color:KA.red,marginBottom:2},
  currentName:{fontSize:22,fontWeight:300,color:KA.navy,fontFamily:"Georgia,serif"},
  remainRow:{display:"flex",justifyContent:"space-between",marginBottom:6},
  remainText:{fontSize:12,color:"rgba(35,60,95,0.6)"},
  remainPct:{fontSize:12,color:"rgba(35,60,95,0.4)"},
  progressTrack:{height:3,background:"rgba(35,60,95,0.1)",borderRadius:2},
  progressFill:{height:"100%",background:KA.red,borderRadius:2,transition:"width 30s linear"},
  dayRow:{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderBottom:`1px solid rgba(162,145,100,0.2)`},
  dayBadge:{background:KA.navy,borderRadius:4,padding:"6px 14px",flexShrink:0},
  dayLetter:{fontSize:14,fontWeight:700,color:KA.gold,fontFamily:"Georgia,serif"},
  chips:{display:"flex",gap:4},
  chip:{fontSize:12,fontWeight:600,padding:"3px 9px",borderRadius:3,color:"rgba(35,60,95,0.35)",border:`1px solid rgba(35,60,95,0.15)`,fontFamily:"Georgia,serif"},
  chipActive:{background:KA.red,color:KA.white,border:`1px solid ${KA.red}`},
  chipNext:{background:"rgba(162,145,100,0.2)",color:KA.gold,border:`1px solid rgba(162,145,100,0.4)`},
  list:{flex:1,padding:"0 16px 24px"},
  listHeader:{fontSize:10,fontWeight:700,letterSpacing:2.5,textTransform:"uppercase",color:KA.red,padding:"14px 0 8px",borderBottom:`1px solid rgba(162,145,100,0.25)`,marginBottom:2},
  listRow:{display:"flex",alignItems:"center",gap:10,padding:"10px 8px",borderBottom:`1px solid rgba(162,145,100,0.1)`,borderRadius:3},
  listRowCur:{background:KA.white,borderLeft:`4px solid ${KA.red}`,paddingLeft:4,boxShadow:"0 1px 6px rgba(35,60,95,0.08)"},
  listRowNext:{background:"rgba(255,255,255,0.7)",borderLeft:`4px solid ${KA.gold}`,paddingLeft:4},
  listRowPast:{opacity:0.35},
  listTime:{fontFamily:"monospace",fontSize:12,color:"rgba(35,60,95,0.45)",width:52,flexShrink:0},
  listPill:{fontWeight:700,padding:"2px 7px",borderRadius:2,minWidth:36,textAlign:"center",flexShrink:0,fontFamily:"Georgia,serif"},
  listName:{fontSize:14,color:KA.navy,flex:1,fontWeight:300},
  listNowTag:{fontSize:9,fontWeight:700,letterSpacing:1.5,color:KA.red},
  listNextTag:{fontSize:9,fontWeight:700,letterSpacing:1.5,color:KA.gold},
  announceBar:{margin:"12px 16px 0",padding:"12px 14px",borderRadius:8,fontSize:15,fontWeight:600,textAlign:"center",lineHeight:1.3},
  menuCard:{margin:"16px 16px 0",background:KA.white,borderRadius:8,border:`1px solid rgba(162,145,100,0.25)`,padding:"12px 14px 14px",boxShadow:"0 2px 12px rgba(35,60,95,0.08)"},
  menuHeader:{fontSize:10,fontWeight:700,letterSpacing:2.5,textTransform:"uppercase",color:KA.red,paddingBottom:8,borderBottom:`1px solid rgba(162,145,100,0.25)`,marginBottom:4},
  menuRow:{display:"flex",alignItems:"baseline",gap:10,padding:"6px 0",borderBottom:`1px solid rgba(162,145,100,0.1)`},
  menuCat:{fontSize:10,fontWeight:600,letterSpacing:1.5,textTransform:"uppercase",color:KA.gold,width:88,flexShrink:0},
  menuItem:{fontSize:15,color:KA.navy,flex:1},
  featCard:{margin:"16px 16px 0",background:KA.white,borderRadius:8,border:`1px solid rgba(162,145,100,0.25)`,padding:"16px 16px 18px",boxShadow:"0 2px 12px rgba(35,60,95,0.08)",textAlign:"center"},
  featEyebrow:{fontSize:10,fontWeight:700,letterSpacing:2.5,textTransform:"uppercase",color:KA.red,marginBottom:12},
  featPhoto:{width:140,height:140,borderRadius:8,objectFit:"cover",border:`3px solid ${KA.gold}`,display:"inline-block"},
  featName:{fontSize:20,fontWeight:400,color:KA.navy,fontFamily:"Georgia,serif",margin:"10px 0 6px",lineHeight:1.15},
  featBio:{fontSize:14,color:"rgba(35,60,95,0.75)",lineHeight:1.4,whiteSpace:"pre-line"},
};

// ─── EVENT BOARD STYLES (Mobile) ───────────────────────────────────────────────
const evMob = {
  wrap:{padding:"16px 16px 28px"},
  head:{marginBottom:12},
  title:{fontSize:24,fontWeight:400,color:KA.navy,fontFamily:"Georgia,serif",lineHeight:1.1},
  sub:{fontSize:12,letterSpacing:2,textTransform:"uppercase",color:KA.gold,fontWeight:600,marginTop:6},
  poster:{width:"100%",maxHeight:380,borderRadius:8,border:`1px solid rgba(162,145,100,0.25)`,marginBottom:14,display:"block"},
  row:{padding:"12px 10px",borderBottom:`1px solid rgba(162,145,100,0.15)`,borderRadius:4},
  rowNow:{background:KA.white,borderLeft:`4px solid ${KA.red}`,paddingLeft:6,boxShadow:"0 1px 6px rgba(35,60,95,0.08)"},
  rowPast:{opacity:0.45},
  rowTop:{display:"flex",alignItems:"center",gap:8,marginBottom:4},
  time:{fontFamily:"monospace",fontSize:13,fontWeight:600,color:KA.navy},
  date:{fontSize:11,fontWeight:600,letterSpacing:1,textTransform:"uppercase",color:KA.gold},
  badge:{fontSize:9,fontWeight:700,letterSpacing:1.5,padding:"2px 8px",borderRadius:2,marginLeft:"auto"},
  badgeNow:{background:KA.red,color:KA.white},
  badgeNext:{background:"rgba(162,145,100,0.2)",color:KA.navy,border:`1px solid rgba(162,145,100,0.45)`},
  name:{fontSize:16,fontWeight:300,color:KA.navy,fontFamily:"Georgia,serif",lineHeight:1.2},
  meta:{fontSize:13,color:"rgba(35,60,95,0.6)",marginTop:3},
  loc:{fontWeight:600,color:KA.gold},
  note:{fontStyle:"italic"},
};